import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, IconButton, Radio, RadioGroup, Stack, Tooltip, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { SendRoomInviteLink, SendRoomInviteLinkByMessage, SendRoomInviteLinkToSelf } from "../../Common/api.routes";
import DialPad from "../../Common/dialPad";
import { currentHost } from "../../Common/helper";
import { isAuthenticated, ValidatePhoneNumber } from "../../Common/helperFunction";
import CadisTextField from "../../Components/Input/CadisTextField";
import CadisLoader from "../../Components/Loading/CadisLoader";
import { useSocketContext } from "../../context/SocketContext";
import apis from "../../HttpConfig/Api";

const InvitePeopleDialog = ({ open, setInviteDialog }: { open: boolean, setInviteDialog: Dispatch<SetStateAction<boolean>> }) => {

  const { t } = useTranslation();
  const [emailAddress, setEmailAddress] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [selectedOption, setSelectedOption] = useState('SMS');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isNumberValid, setIsNumberValid] = useState(true);
  const [phoneNumberErrorMsg, setPhoneNumberErrorMsg] = useState<string>('');
  const [wastInviteSent, setWastInviteSent] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [tooltipText, setTooltipText] = useState(t('Copy'));
  const [loading, setLoading] = useState(false);
  const user = isAuthenticated();
  const [selfEmailAddress, setSelfEmailAddress] = useState(user?.userEmail);
  const { sessionIdRef, isWAConferenceCallRef, notificationSentToneRef, wastInvitationLink } = useSocketContext();

  const handleCloseInviteDialog = () => {
    setEmailAddress('');
    setPhoneNumber('')
    setPhoneNumberErrorMsg('');
    setSelfEmailAddress('');
    setIsNumberValid(true);
    setIsValid(true);
    setIsEmailValid(true);
    setSelectedOption("SMS");
    setInviteDialog(false);
    isWAConferenceCallRef.current = false;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(wastInvitationLink);
      setTooltipText(t('lbl_copid'));
      setTimeout(() => setTooltipText('copy'), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const handleEmailChange = (e: any) => {
    setEmailAddress(e.target.value);
    setIsValid(true);
  };

  const handleEmailBlur = () => {
    setIsValid(isValidEmail(emailAddress));
  };

  const handleSendInvite = async () => {
    if (selectedOption === 'SMS') {
      if (!ValidatePhoneNumber(phoneNumber, setPhoneNumberErrorMsg)) {
        setIsNumberValid(false);
        return;
      }
    } else {
      if (selectedOption === 'Email') {
        if (!isValidEmail(emailAddress)) {
          setIsValid(false);
          return;
        }
      }
      else {
        if (!isValidEmail(selfEmailAddress)) {
          setIsEmailValid(false);
          return;
        }
      }
    }

    let roomLink = currentHost + "/webAssistantJoin/";

    setLoading(true);
    if (selectedOption === 'Email') {
      const data = {
        "wastEmail": emailAddress,
        "roomLink": roomLink,
        "sessionId": sessionIdRef.current
      }
      try {
        const response = await apis.instance.post(`${SendRoomInviteLink}`, data)
        if (response.data?.success) {
          playSound();
          toast.success("Invitation sent successfully.");
          setInviteDialog(false);
          setEmailAddress('');
          setSelfEmailAddress('');
          setLoading(false);
          setSelectedOption("SMS");
          setWastInviteSent(true);
          isWAConferenceCallRef.current = true;
        }
      } catch (error: any) {
        toast.error(error.message);
        setLoading(false);
      }
    } else if (selectedOption === 'Self') {
      const data = {
        "consultantEmail": selfEmailAddress,
        "consultantUserId": user?.userId,
        "roomLink": roomLink,
        "sessionId": sessionIdRef.current
      }
      try {
        const response = await apis.instance.post(`${SendRoomInviteLinkToSelf}`, data)
        if (response.data?.success) {
          playSound();
          toast.success("Invitation sent successfully.");
          setInviteDialog(false);
          setEmailAddress('');
          setLoading(false);
          setSelectedOption("SMS");
          setSelfEmailAddress('');
          setWastInviteSent(true);
          isWAConferenceCallRef.current = true;
        }
      } catch (error: any) {
        toast.error(error.message);
        setLoading(false);
      }
    } else {
      const data = {
        "phoneNumber": phoneNumber,
        "roomLink": roomLink,
        "sessionId": sessionIdRef.current
      }
      try {
        const response = await apis.instance.post(`${SendRoomInviteLinkByMessage}`, data)
        if (response.data?.success) {
          playSound();
          toast.success("Invitation sent successfully.");
          setInviteDialog(false);
          setPhoneNumber('');
          setLoading(false);
          setSelectedOption("SMS");
          setWastInviteSent(true);
          isWAConferenceCallRef.current = true;
        }
      } catch (error: any) {
        toast.error(error.message);
        setLoading(false);
      }
    }
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleRadioChange = (event: any) => {
    setPhoneNumber('');
    setEmailAddress('');
    setIsNumberValid(true);
    setIsValid(true);
    setIsEmailValid(true);
    setSelectedOption(event.target.value);
  };

  const playSound = () => {
    if (notificationSentToneRef.current) {
      notificationSentToneRef.current.currentTime = 0;
      notificationSentToneRef.current.play();
    }
  };

  return (
    <Dialog className="invite-user-dialog"
      open={open}
      onClose={() => { return null }}
      aria-labelledby="invite-dialog-title"
      aria-describedby="invite-dialog-description">
      <DialogTitle id="invite-dialog-title" >
        {t('lbl_invitePeople')}
      </DialogTitle>
      <DialogContent>
        <FormControl variant="outlined" fullWidth>
          <RadioGroup
            aaria-labelledby="Invite-radio-buttons-group-label"
            value={selectedOption}
            onChange={handleRadioChange}
          >
            <FormControlLabel value="SMS" control={<Radio />} label="Invite through SMS (Text)" />
            {selectedOption === 'SMS' &&
              <DialPad
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
                selectedOption={selectedOption}
                phoneNumberErrorMsg={phoneNumberErrorMsg}
                setPhoneNumberErrorMsg={setPhoneNumberErrorMsg}
                isNumberValid={isNumberValid}
                setIsNumberValid={setIsNumberValid}
                handleCloseInviteDialog={handleCloseInviteDialog}
                handleSendInvite={handleSendInvite}
                loading={loading}
              />
            }
            <FormControlLabel value="Email" control={<Radio />} label="Invite through Email" />
            {selectedOption === 'Email' &&
              <Box mb={1}>
                <CadisTextField
                  id="inviteEmail"
                  name="inviteEmail"
                  value={emailAddress}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  error={!isValid}
                  helperText={!isValid && selectedOption === 'Email' ? 'Invalid email address' : ''}
                  placeholder="Enter Email"
                />
                <Box paddingTop={3.5} paddingX={0} justifyContent={"flex-end"} display={'flex'} columnGap={1}>
                  <Button variant="contained" color="success" onClick={handleSendInvite} disabled={loading} endIcon={loading ? <CadisLoader color='primary' size={14} /> : ''}>
                    {t('lbl_invite')}
                  </Button>
                  <Button variant="contained" color="error" onClick={handleCloseInviteDialog}>
                    {t('lbl_cancel')}
                  </Button>
                </Box>
                <Divider sx={{ mt: 2, mb: 0 }} />
              </Box>

            }
            <FormControlLabel value="Self" control={<Radio />} label="Copy Link to Share" />
            {selectedOption === 'Self' && (
              <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" width="100%">
                <Typography variant="body1" color="textSecondary" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flexGrow: 1 }}>
                  {wastInvitationLink}
                </Typography>
                <Stack alignItems="center" spacing={0.5}>
                  <Tooltip title={tooltipText} placement="right-start">
                    <IconButton aria-label="Copy" onClick={copyToClipboard} color="inherit" size="small">
                      <span className="cad-copy-icon"></span>
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            )}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      {selectedOption === 'Self' &&
        <DialogActions>
          <Button variant="contained" color="error" onClick={handleCloseInviteDialog}>
            {t('lbl_cancel')}
          </Button>
        </DialogActions>
      }
    </Dialog>
  )
}

export default InvitePeopleDialog;